import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { Modal, Input, Button, Spin, message } from 'antd'
import styled from 'styled-components'
import { API } from 'src/utils/interceptor'
import CreateNewUserModal from 'src/components/CreateNewUserModal'
import { Auth } from 'aws-amplify'

const initialState = {
  isLoading: false,
  owner: null,
  additionalUsers: [],
  error: null,
}

const VenueUsersModal = ({ visible, onClose, venue, supplierName }) => {
  const [inviteEmail, setInviteEmail] = useState('')
  const [state, setState] = useState(initialState)
  const [inviteLoading, setInviteLoading] = useState(false)
  const [removeLoadingId, setRemoveLoadingId] = useState(null)
  const [confirmVisible, setConfirmVisible] = useState(false)
  const [showCreateUserModal, setShowCreateUserModal] = useState(false)
  const [pendingUserEmail, setPendingUserEmail] = useState('')
  const [isCreatingUser, setIsCreatingUser] = useState(false)
  const [createUserError, setCreateUserError] = useState('')

  const venueName =
    venue?.venueTitle || venue?.title || venue?.name || 'this venue'
  const venueAddress = [
    venue?.addressLine1,
    venue?.addressLine2,
    venue?.addressCity,
    venue?.addressPostCode,
  ]
    .filter(Boolean)
    .join(', ')

  const owner = useMemo(() => state.owner, [state.owner])
  const additionalUsers = useMemo(
    () => state.additionalUsers,
    [state.additionalUsers],
  )

  const fetchUsersForVenue = useCallback(async venueId => {
    setState(prev => ({ ...prev, isLoading: true, error: null }))
    try {
      const response = await API.get('api', `venue/${venueId}`)
      const ownerData = response?.account
        ? {
            name: response.account.name || 'Primary owner',
            email: response.account.emailAddress || '—',
            note: 'Added on venue creation',
          }
        : null
      setState({
        isLoading: false,
        owner: ownerData,
        additionalUsers: response?.additionalUsers || [],
        error: null,
      })
    } catch (error) {
      console.error('Failed to load venue users', error)
      setState({
        isLoading: false,
        owner: null,
        additionalUsers: [],
        error: 'Could not load venue users. Please try again later.',
      })
    }
  }, [])

  useEffect(() => {
    if (!visible || !venue?._id) {
      setState(initialState)
      setInviteEmail('')
      return
    }

    fetchUsersForVenue(venue._id)
  }, [fetchUsersForVenue, visible, venue?._id])

  const handleInviteUser = async () => {
    if (!inviteEmail.trim() || !venue?._id) {
      return
    }
    setInviteLoading(true)
    try {
      await API.post('api2', 'admin/checkEmailExists', {
        body: {
          email: inviteEmail.trim(),
          venueAddress,
        },
      })
      setConfirmVisible(true)
    } catch (error) {
      if (error.response?.status === 404) {
        setPendingUserEmail(inviteEmail.trim())
        setShowCreateUserModal(true)
        setCreateUserError('')
      } else {
        const msg =
          error.response?.data?.message ||
          'Unable to send invite. Please try again later.'
        message.error(msg)
      }
    } finally {
      setInviteLoading(false)
    }
  }

  const sendInviteToExistingUser = async () => {
    if (!inviteEmail.trim() || !venue?._id) return
    setInviteLoading(true)
    try {
      await API.post('api2', 'admin/addUserToVenue', {
        body: {
          email: inviteEmail.trim(),
          venueId: venue._id,
          supplierInvite: true,
          supplierName,
        },
      })
      message.success('Invite sent.')
      setInviteEmail('')
      setConfirmVisible(false)
      fetchUsersForVenue(venue._id)
    } catch (error) {
      const msg =
        error.response?.data?.message ||
        'Unable to send invite. Please try again later.'
      message.error(msg)
    } finally {
      setInviteLoading(false)
    }
  }

  const handleCreateNewUser = async userData => {
    setIsCreatingUser(true)
    setCreateUserError('')

    try {
      await Auth.signUp({
        username: userData.email,
        password: userData.password,
        attributes: {
          name: userData.name,
        },
      })

      const emailResponse = await API.post('api2', 'admin/sendWelcomeEmail', {
        body: {
          userName: userData.name,
          userEmail: userData.email,
          userPassword: userData.password,
          venueName: venueName,
          venueId: venue?._id,
          supplierInvite: true,
          supplierName,
        },
      })

      if (!emailResponse.success) {
        throw new Error(emailResponse.message || 'Failed to send welcome email')
      }

      message.success(
        `User ${userData.name} (${userData.email}) created and invited successfully.`,
      )
      setShowCreateUserModal(false)
      setInviteEmail('')
      setPendingUserEmail('')
      fetchUsersForVenue(venue._id)
    } catch (error) {
      console.error('Error creating user:', error)
      let errorMessage = 'Failed to create user. Please try again.'
      if (error.code === 'UsernameExistsException') {
        errorMessage = 'A user with this email already exists.'
      } else if (error.code === 'InvalidPasswordException') {
        errorMessage = error.message || 'Password does not meet requirements.'
      } else if (error.message) {
        errorMessage = error.message
      }
      setCreateUserError(errorMessage)
    } finally {
      setIsCreatingUser(false)
    }
  }

  const handleCreateUserCancel = () => {
    setShowCreateUserModal(false)
    setCreateUserError('')
    setInviteEmail('')
    setPendingUserEmail('')
  }

  const handleRemoveUser = async user => {
    if (!venue?._id || !user?._id) return
    Modal.confirm({
      title: 'Remove user access',
      content: `Are you sure you want to remove ${user.emailAddress} from ${venueName}?`,
      okText: 'Remove',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        setRemoveLoadingId(user._id)
        try {
          await API.post('api2', 'admin/removeUserFromVenue', {
            body: {
              accountId: user._id,
              venueId: venue._id,
            },
          })
          message.success('User removed.')
          setState(prev => ({
            ...prev,
            additionalUsers: prev.additionalUsers.filter(
              u => u._id !== user._id,
            ),
          }))
        } catch (error) {
          console.error('Failed to remove user', error)
          const msg =
            error.response?.data?.message || 'Unable to remove user right now.'
          message.error(msg)
        } finally {
          setRemoveLoadingId(null)
        }
      },
    })
  }

  return (
    <Modal
      visible={visible}
      onCancel={onClose}
      footer={null}
      width={780}
      destroyOnClose
      title={null}
    >
      <ModalBody>
        <ModalTitle>Venue Users</ModalTitle>
        <ModalDescription>
          Manage who has access to {venueName}.
        </ModalDescription>

        <Section>
          <SectionHeader>Primary Owner</SectionHeader>
          {state.isLoading ? (
            <LoadingState>
              <Spin />
              <span>Loading users…</span>
            </LoadingState>
          ) : owner ? (
            <PrimaryOwnerCard>
              <OwnerInfo>
                <OwnerName>{owner.name}</OwnerName>
                <OwnerEmail>{owner.email}</OwnerEmail>
                <OwnerTag>Primary Owner</OwnerTag>
                <OwnerNote>{owner.note}</OwnerNote>
              </OwnerInfo>
              <SupportNote>
                Contact OMS Support at{' '}
                <a href='mailto:info@ordersmadesimple.com'>
                  info@ordersmadesimple.com
                </a>{' '}
                to change primary owner.
              </SupportNote>
            </PrimaryOwnerCard>
          ) : state.error ? (
            <ErrorState>{state.error}</ErrorState>
          ) : (
            <PrimaryOwnerCard>
              <OwnerInfo>
                <OwnerName>No owner data yet</OwnerName>
                <OwnerEmail>—</OwnerEmail>
                <OwnerTag>Primary Owner</OwnerTag>
                <OwnerNote>
                  We’ll show the primary owner once venue data syncs.
                </OwnerNote>
              </OwnerInfo>
              <SupportNote>
                Contact OMS Support at{' '}
                <a href='mailto:info@ordersmadesimple.com'>
                  info@ordersmadesimple.com
                </a>{' '}
                to change primary owner.
              </SupportNote>
            </PrimaryOwnerCard>
          )}
        </Section>

        <Section>
          <SectionHeader>Additional Users</SectionHeader>
          <InviteForm>
            <Input
              placeholder='Enter user email address'
              value={inviteEmail}
              onChange={e => setInviteEmail(e.target.value)}
              onPressEnter={handleInviteUser}
            />
            <Button
              type='primary'
              onClick={handleInviteUser}
              loading={inviteLoading}
              disabled={!inviteEmail.trim()}
            >
              Invite User
            </Button>
          </InviteForm>

          {state.isLoading ? (
            <LoadingState>
              <Spin />
              <span>Loading users…</span>
            </LoadingState>
          ) : additionalUsers.length ? (
            <UsersGrid>
              {additionalUsers.map(user => (
                <UserCard key={user._id || user.emailAddress}>
                  <UserDetails>
                    <UserName>{user.name || 'Team member'}</UserName>
                    <UserEmail>{user.emailAddress || '—'}</UserEmail>
                    <UserTag>Additional User</UserTag>
                  </UserDetails>
                  <Button
                    danger
                    loading={removeLoadingId === user._id}
                    onClick={() => handleRemoveUser(user)}
                  >
                    Remove
                  </Button>
                </UserCard>
              ))}
            </UsersGrid>
          ) : (
            <UsersEmptyState>
              <strong>No additional users yet.</strong>
              <span>
                Once the new API is connected you’ll be able to invite your team
                here.
              </span>
            </UsersEmptyState>
          )}
        </Section>
      </ModalBody>

      <ConfirmModal
        visible={confirmVisible}
        email={inviteEmail}
        venueName={
          venue?.venueTitle || venue?.title || venue?.name || 'this venue'
        }
        supplierName={supplierName}
        loading={inviteLoading}
        onCancel={() => {
          setConfirmVisible(false)
          setInviteLoading(false)
        }}
        onConfirm={sendInviteToExistingUser}
      />

      <CreateNewUserModal
        visible={showCreateUserModal}
        onCancel={handleCreateUserCancel}
        onSubmit={handleCreateNewUser}
        loading={isCreatingUser}
        error={createUserError}
        prefilledEmail={pendingUserEmail}
      />
    </Modal>
  )
}

const ConfirmModal = ({
  visible,
  email,
  venueName,
  supplierName,
  onCancel,
  onConfirm,
  loading,
}) => (
  <Modal
    visible={visible}
    title='Invite Existing User'
    onCancel={onCancel}
    footer={[
      <Button key='cancel' onClick={onCancel}>
        Cancel
      </Button>,
      <Button
        key='confirm'
        type='primary'
        loading={loading}
        onClick={onConfirm}
      >
        Send Invitation
      </Button>,
    ]}
    width={520}
  >
    <p>
      <strong>{email}</strong> already has an OMS account. We&apos;ll add their
      existing account to <strong>{venueName}</strong> so they can order from{' '}
      {supplierName}.
    </p>
    <p style={{ fontWeight: '600', marginBottom: 8 }}>What happens next:</p>
    <ul style={{ paddingLeft: 20 }}>
      <li>The next time they log in, they&apos;ll see {venueName}.</li>
      <li>
        They&apos;ll be able to place orders, manage pricing, and stay in sync
        with you.
      </li>
      <li>We&apos;ll email them to let them know about the new access.</li>
    </ul>
  </Modal>
)

const ModalBody = styled.div`
  padding: 8px 4px 16px;
`

const ModalTitle = styled.h3`
  margin: 0;
  font-size: 22px;
  font-weight: 700;
`

const ModalDescription = styled.p`
  margin: 8px 0 24px 0;
  color: #6b7280;
`

const Section = styled.div`
  & + & {
    margin-top: 32px;
  }
`

const SectionHeader = styled.h4`
  margin: 0 0 16px 0;
  font-size: 16px;
  font-weight: 600;
  color: #0f172a;
`

const PrimaryOwnerCard = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 20px;
  background: #fff;
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const OwnerInfo = styled.div``

const OwnerName = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #111827;
`

const OwnerEmail = styled.div`
  color: #4b5563;
  margin-top: 4px;
`

const OwnerTag = styled.span`
  display: inline-block;
  margin-top: 12px;
  padding: 4px 10px;
  border-radius: 999px;
  background: #e6f7ff;
  color: #0ea5e9;
  font-size: 12px;
  font-weight: 600;
`

const OwnerNote = styled.div`
  font-size: 12px;
  color: #94a3b8;
  margin-top: 8px;
`

const SupportNote = styled.div`
  font-size: 13px;
  color: #6b7280;

  a {
    color: #0ea5e9;
  }
`

const InviteForm = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  margin-bottom: 20px;

  button {
    height: 40px;
  }
`

const UsersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 16px;
`

const UserCard = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 16px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  background: #fff;
`

const UserDetails = styled.div`
  flex: 1;
`

const UserName = styled.div`
  font-weight: 600;
  color: #111827;
`

const UserEmail = styled.div`
  color: #4b5563;
  font-size: 14px;
  margin-top: 4px;
`

const UserTag = styled.span`
  display: inline-block;
  margin-top: 12px;
  padding: 4px 10px;
  border-radius: 999px;
  background: #ecfccb;
  color: #4d7c0f;
  font-size: 12px;
  font-weight: 600;
`

const UsersEmptyState = styled.div`
  border: 1px dashed #d1d5db;
  border-radius: 12px;
  padding: 24px;
  text-align: center;
  color: #6b7280;
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const LoadingState = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  color: #6b7280;
  padding: 16px;
`

const ErrorState = styled.div`
  border: 1px solid #fecaca;
  background: #fef2f2;
  color: #dc2626;
  border-radius: 12px;
  padding: 16px;
`

export default VenueUsersModal
